import { useEffect, useState } from "react";
import {
  createContactSubmission,
  defaultContactDetails,
  fetchContactDetails,
  sendEnquiryToWhatsApp,
} from "../services/siteService";
import { BRANCHES, PROGRAM_OPTIONS, SCHOOL_HOURS } from "../data/siteContent";

const emptyForm = {
  parentName: "",
  childName: "",
  childAge: "",
  phoneNumber: "",
  email: "",
  program: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [contactDetails, setContactDetails] = useState(defaultContactDetails);
  const [whatsAppUrl, setWhatsAppUrl] = useState("");
  const [isWhatsAppBlocked, setIsWhatsAppBlocked] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadContactDetails() {
      try {
        const details = await fetchContactDetails();
        if (isMounted) {
          setContactDetails(details);
        }
      } catch (error) {
        if (isMounted) {
          setContactDetails(defaultContactDetails);
        }
      }
    }

    loadContactDetails();

    return () => {
      isMounted = false;
    };
  }, []);

  const branchLocationText = BRANCHES.map((branch) => `${branch.name}: ${branch.address}`).join(" | ");
  const openingHoursText = `${SCHOOL_HOURS.statusLabel}: ${SCHOOL_HOURS.openToday} | ${SCHOOL_HOURS.schoolTime} | ${SCHOOL_HOURS.sunday}`;

  const contactCards = [
    { icon: "📞", label: "Contact Us", value: contactDetails.phone, bg: "bg-teal-700" },
    { icon: "✉️", label: "Email Address", value: contactDetails.email, bg: "bg-teal-700" },
    { icon: "📍", label: "Our Locations", value: branchLocationText, bg: "bg-teal-700" },
    { icon: "🕐", label: "Opening Hours", value: openingHoursText, bg: "bg-teal-700" },
  ];

  const socials = contactDetails.socials || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.parentName.trim()) newErrors.parentName = "Parent name is required";
    if (!form.childName.trim()) newErrors.childName = "Child name is required";
    if (!form.childAge) newErrors.childAge = "Child age is required";
    if (!form.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phoneNumber.replace(/\D/g, ""))) {
      newErrors.phoneNumber = "Enter a valid 10-digit number";
    }
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!form.program) newErrors.program = "Please select a program";
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const enquiryPayload = { ...form, source: "contact-page" };

    setIsSubmitting(true);
    setSubmitError("");

    try {
      await createContactSubmission(enquiryPayload);
      const whatsappResult = await sendEnquiryToWhatsApp(enquiryPayload);
      setWhatsAppUrl(whatsappResult.url);
      setIsWhatsAppBlocked(!whatsappResult.opened && Boolean(whatsappResult.url));
      setSubmitted(true);
      setForm(emptyForm);
      setErrors({});
    } catch (error) {
      setSubmitError(error.message || "Unable to submit the contact form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <div className="bg-[rgb(122,49,125)] px-6 py-14 text-center text-white">
        <p className="mb-2 text-sm uppercase tracking-widest text-teal-300">Our Programs</p>
        <h1 className="text-3xl font-extrabold md:text-5xl">Contact Us</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-teal-200 md:text-base">
          Nurturing every child's unique potential through play, creativity, and guided learning.
        </p>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-6 pt-12">
        <span className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-orange-200 px-3 py-1 text-xs font-semibold text-orange-500">
          <span>*</span> Contact Us
        </span>

        <div className="flex flex-col gap-12 lg:flex-row lg:gap-16">
          <div className="flex-1">
            <h1 className="mb-5 text-3xl font-extrabold leading-tight text-gray-900 md:text-4xl xl:text-5xl">
              {contactDetails.heroTitle}
            </h1>
            <p className="mb-8 max-w-md text-sm leading-relaxed text-gray-500 md:text-base">
              {contactDetails.heroDescription}
            </p>

            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contactCards.map((card, i) => (
                <div
                  key={`${card.label}-${i}`}
                  className="flex items-center gap-4 rounded-2xl border border-gray-200 px-5 py-4 transition-shadow hover:shadow-sm"
                >
                  <div className={`${card.bg} flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-lg text-white`}>
                    {card.icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{card.label}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-semibold text-gray-700">Follow The Social Media:</span>
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  title={social.label}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-300 text-sm font-bold text-gray-500 transition-all hover:border-teal-600 hover:text-teal-600"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="max-w-xl flex-1">
            <div
              className="rounded-3xl p-6 md:p-8"
              style={{
                background: "linear-gradient(135deg, #f0fdf8 0%, #ecfdf5 50%, #f0f9ff 100%)",
              }}
            >
              <h2 className="mb-6 text-2xl font-extrabold text-gray-900 md:text-3xl">
                Enquiry for Preschool Admissions
              </h2>
              <p className="-mt-3 mb-4 text-sm text-gray-500">{contactDetails.cityLabel}</p>

              {submitted ? (
                <div className="py-12 text-center">
                  <span className="mb-4 block text-5xl">🎉</span>
                  <p className="mb-2 text-xl font-bold text-teal-700">Enquiry Submitted!</p>
                  <p className="text-sm text-gray-500">Thank you! We'll be in touch shortly.</p>
                  {isWhatsAppBlocked && whatsAppUrl ? (
                    <a
                      href={whatsAppUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
                    >
                      Open WhatsApp
                    </a>
                  ) : null}
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm(emptyForm);
                      setErrors({});
                      setSubmitError("");
                      setWhatsAppUrl("");
                      setIsWhatsAppBlocked(false);
                    }}
                    className="mt-6 text-sm text-teal-600 underline"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {submitError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                      {submitError}
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700">
                      Parent Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="parentName"
                      value={form.parentName}
                      onChange={handleChange}
                      placeholder="Enter parent's full name"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-800 placeholder-gray-400 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                        errors.parentName ? "border-red-500" : "border-gray-200"
                      }`}
                    />
                    {errors.parentName && <p className="mt-1 text-xs text-red-500">{errors.parentName}</p>}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700">
                      Child Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="childName"
                      value={form.childName}
                      onChange={handleChange}
                      placeholder="Enter child's full name"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-800 placeholder-gray-400 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                        errors.childName ? "border-red-500" : "border-gray-200"
                      }`}
                    />
                    {errors.childName && <p className="mt-1 text-xs text-red-500">{errors.childName}</p>}
                  </div>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                      <label className="mb-1 block text-sm font-semibold text-gray-700">
                        Child Age <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="childAge"
                        value={form.childAge}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-800 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                          errors.childAge ? "border-red-500" : "border-gray-200"
                        }`}
                      >
                        <option value="">Select age</option>
                        <option value="1.5 - 2 Years">1.5 - 2 Years</option>
                        <option value="2 - 3 Years">2 - 3 Years</option>
                        <option value="3 - 4 Years">3 - 4 Years</option>
                        <option value="4 - 5 Years">4 - 5 Years</option>
                        <option value="5 - 6 Years">5 - 6 Years</option>
                      </select>
                      {errors.childAge && <p className="mt-1 text-xs text-red-500">{errors.childAge}</p>}
                    </div>

                    <div className="flex-1">
                      <label className="mb-1 block text-sm font-semibold text-gray-700">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-orange-500" />
                        <input
                          name="phoneNumber"
                          value={form.phoneNumber}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          className={`w-full rounded-xl border bg-white py-3 pl-7 pr-4 text-sm text-gray-800 placeholder-gray-400 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                            errors.phoneNumber ? "border-red-500" : "border-gray-200"
                          }`}
                        />
                      </div>
                      {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      type="email"
                      placeholder="Enter email address"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-800 placeholder-gray-400 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                        errors.email ? "border-red-500" : "border-gray-200"
                      }`}
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700">
                      Program <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="program"
                      value={form.program}
                      onChange={handleChange}
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-800 transition focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                        errors.program ? "border-red-500" : "border-gray-200"
                      }`}
                    >
                      <option value="">Select a program</option>
                      {PROGRAM_OPTIONS.map((program) => (
                        <option key={program} value={program}>
                          {program}
                        </option>
                      ))}
                    </select>
                    {errors.program && <p className="mt-1 text-xs text-red-500">{errors.program}</p>}
                  </div>

                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                      This site is protected by reCAPTCHA and the Google{" "}
                      <a href="#" className="text-teal-600 hover:underline">Privacy Policy</a> and{" "}
                      <a href="#" className="text-teal-600 hover:underline">Terms of Service</a> apply.
                    </p>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-orange-500 px-8 py-3.5 text-sm font-bold text-white shadow-md transition-all duration-200 hover:bg-orange-600 hover:shadow-lg active:scale-95 disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-7xl px-4 pb-16">
        <h2 className="mb-8 text-center text-3xl font-extrabold text-gray-900 md:text-4xl">
          Reach out and let's weave success together
        </h2>
        <div className="mb-6 grid gap-4 md:grid-cols-2">
          {BRANCHES.map((branch) => (
            <div key={branch.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-bold text-teal-700">{branch.name}</p>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{branch.address}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-orange-500">Phone: {branch.phone}</p>
            </div>
          ))}
        </div>
        <div className="overflow-hidden rounded-3xl border border-gray-200 shadow-sm">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d486.2019223090278!2d77.68671075379275!3d12.868100688956712!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae6d44f1b58103%3A0xf67a39f87c031a3a!2sSeekers%20Bay%20Preschool%20-%20Rayasandra%20Gattahalli%3A%20Premier%20Preschool%20%26%20Day%20Care!5e0!3m2!1sen!2sin!4v1777036666529!5m2!1sen!2sin"
              className="absolute left-0 top-0 h-full w-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Seekers Bay Preschool Location Map"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}
